import React, { useEffect, useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import ManagerSidebar from '../../../components/Sidebar/ManagerSidebar';
import './Reports.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload, faUsers, faMoneyBillWave
} from '@fortawesome/free-solid-svg-icons';

// ===============================
// Utility: Professional PDF Branding
// ===============================
const addPdfBranding = (doc, reportTitle) => {
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("GKM INVEST COMPANY LTD", 14, 25);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(220, 220, 220);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 140, 25);

    doc.setTextColor(30, 41, 59);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(reportTitle.toUpperCase(), 14, 55);

    doc.setDrawColor(37, 99, 235);
    doc.setLineWidth(0.8);
    doc.line(14, 58, 80, 58);
};

// ===============================
// Expenses Report Component
// ===============================
export const ExpensesReport = () => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    useEffect(() => { fetchExpenses(); }, [startDate, endDate, categoryFilter]);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const res = await axios.get('https://ikaze-thouse-backend.vercel.app/api/v1/expenses/getAll');
            if (res.data.success) {
                let filtered = res.data.data;
                if (startDate) filtered = filtered.filter(e => new Date(e.date) >= new Date(startDate));
                if (endDate) filtered = filtered.filter(e => new Date(e.date) <= new Date(endDate));
                if (categoryFilter) filtered = filtered.filter(e => e.category === categoryFilter);
                setExpenses(filtered);
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const totalAmount = expenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
    const categories = [...new Set(expenses.map(e => e.category))].filter(Boolean);

    const handleDownloadPDF = () => {
        const doc = new jsPDF();
        addPdfBranding(doc, "Consolidated Expenses Report");

        autoTable(doc, {
            startY: 65,
            head: [["ID", "Description", "Category", "Date", "Amount"]],
            body: expenses.map(e => [
                e.expenseid,
                e.description,
                e.category,
                e.date?.substring(0, 10),
                `${parseFloat(e.amount).toFixed(2)} RWF`
            ]),
            foot: [['', '', '', 'TOTAL OUTGOING', `${totalAmount.toFixed(2)} RWF`]],
            theme: 'striped'
        });

        doc.save(`GKM_Expenses_${Date.now()}.pdf`);
    };

    // ✨ FIXED: EXCEL WITH TITLES INCLUDED
    const handleDownloadExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Expenses Report");

        // 1. Setup explicit column layouts width properties
        sheet.columns = [
            { id: 'A', width: 12 },
            { id: 'B', width: 32 },
            { id: 'C', width: 22 },
            { id: 'D', width: 16 },
            { id: 'E', width: 20 }
        ];

        // 🏢 ROW 1: COMPANY BANNER TITLE
        sheet.mergeCells('A1:E1');
        const titleCell = sheet.getCell('A1');
        titleCell.value = 'GKM INVEST COMPANY LTD';
        titleCell.font = { size: 20, bold: true, color: { argb: 'FFFFFF' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E40AF' } };
        sheet.getRow(1).height = 40;

        // 📊 ROW 2: REPORT SUBTITLE
        sheet.mergeCells('A2:E2');
        const reportTitle = sheet.getCell('A2');
        reportTitle.value = 'EXPENSES FINANCIAL REPORT';
        reportTitle.font = { size: 15, bold: true, color: { argb: '111827' } };
        reportTitle.alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getRow(2).height = 28;

        // 📅 ROW 3: TIMESTAMP LOG
        sheet.mergeCells('A3:E3');
        const dateCell = sheet.getCell('A3');
        dateCell.value = `Report Generated On: ${new Date().toLocaleString()}`;
        dateCell.font = { italic: true, size: 11, color: { argb: '6B7280' } };
        dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getRow(3).height = 22;

        // 📌 ROW 4: DATA HEADERS
        const headerRow = sheet.getRow(4);
        headerRow.values = ['ID', 'Description', 'Category', 'Date', 'Amount (RWF)'];
        headerRow.height = 26;
        headerRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: "FFFFFF" }, size: 11 };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "2563EB" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
        });

        // DATA ROWS (Starts safely at row 5)
        expenses.forEach((e, idx) => {
            const amount = parseFloat(e.amount || 0);
            const row = sheet.addRow([
                e.expenseid,
                e.description,
                e.category,
                e.date?.substring(0, 10),
                amount
            ]);
            row.height = 20;
            row.alignment = { vertical: "middle" };
            row.getCell(1).alignment = { horizontal: "center" };
            row.getCell(4).alignment = { horizontal: "center" };
            row.getCell(5).numFmt = '"RWF" #,##0.00';

            if (idx % 2 === 0) {
                row.eachCell(cell => {
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "F8FAFC" } };
                });
            }
        });

        // 🟩 TOTAL OVERVIEW ROW
        const totalRow = sheet.addRow(['', 'TOTAL EXPENSES', '', '', totalAmount]);
        totalRow.height = 24;
        totalRow.font = { bold: true };
        totalRow.eachCell(cell => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "DCFCE7" } };
            cell.alignment = { vertical: "middle" };
        });
        totalRow.getCell(2).alignment = { horizontal: "left" };
        totalRow.getCell(5).numFmt = '"RWF" #,##0.00';

        // 🧱 STRUCTURAL BORDERS BLOCK (Skips rows 1-3 title spaces cleanly)
        sheet.eachRow((row, rowNum) => {
            if (rowNum < 4) return;
            row.eachCell(cell => {
                cell.border = {
                    top: { style: "thin", color: { argb: "E2E8F0" } },
                    left: { style: "thin", color: { argb: "E2E8F0" } },
                    bottom: { style: "thin", color: { argb: "E2E8F0" } },
                    right: { style: "thin", color: { argb: "E2E8F0" } }
                };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `GKM_Expenses_${Date.now()}.xlsx`);
    };

    return (
        <div className="report-container">
            <ManagerSidebar />
            <main className="report-main">
                <header className="report-header">
                    <div className="header-left">
                        <h1>Financial Statements</h1>
                        <p>Track expenditures and monitor outgoing cash flow.</p>
                    </div>
                    <div className="header-actions" style={{ display: 'flex', gap: '10px' }}>
                        <button className="export-btn" onClick={handleDownloadPDF}>
                            <FontAwesomeIcon icon={faDownload} /> Export PDF
                        </button>
                        <button className="export-btn" onClick={handleDownloadExcel}>
                            <FontAwesomeIcon icon={faDownload} /> Export Excel
                        </button>
                    </div>
                </header>

                <div className="report-grid">
                    <div className="report-card summary-card">
                        <div className="card-icon blue"><FontAwesomeIcon icon={faMoneyBillWave} /></div>
                        <div className="card-data">
                            <label>Total Outgoing</label>
                            <h3>{totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })} RWF</h3>
                        </div>
                    </div>
                    <div className="report-card filter-card">
                        <div className="filter-group">
                            <div className="input-field">
                                <label>From</label>
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                            </div>
                            <div className="input-field">
                                <label>To</label>
                                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                            </div>
                            <div className="input-field">
                                <label>Category</label>
                                <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                                    <option value="">All Categories</option>
                                    {categories.map(c => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="table-wrapper shadow-sm">
                    {loading ? <p>Loading statement metrics...</p> : (
                        <table className="modern-report-table">
                            <thead>
                                <tr><th>ID</th><th>Description</th><th>Category</th><th>Date</th><th>Amount</th></tr>
                            </thead>
                            <tbody>
                                {expenses.map(e => (
                                    <tr key={e.expenseid}>
                                        <td>#{e.expenseid}</td>
                                        <td>{e.description}</td>
                                        <td>{e.category}</td>
                                        <td>{e.date?.substring(0, 10)}</td>
                                        <td>{parseFloat(e.amount).toFixed(2)} RWF</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </main>
        </div>
    );
};

// ===============================
// Tenants Report Component
// ===============================
export const TenantsReport = () => {
    const [tenants, setTenants] = useState([]);
    const [filterByStatus, setFilterByStatus] = useState('current');

    useEffect(() => { fetchTenants(); }, [filterByStatus]);

    const fetchTenants = async () => {
        try {
            const response = await axios.get('https://ikaze-thouse-backend.vercel.app/api/v1/tenants/getAll');
            if (response.data.success) {
                let data = response.data.data;
                const today = new Date();

                if (filterByStatus === 'current') {
                    data = data.filter(t => !t.departure_date || new Date(t.departure_date) >= today);
                } else if (filterByStatus === 'departed') {
                    data = data.filter(t => t.departure_date && new Date(t.departure_date) < today);
                }
                setTenants(data);
            }
        } catch (err) { console.error(err); }
    };

    const totalRent = tenants.reduce((sum, t) => sum + (parseFloat(t.rent) || 0), 0);

    const handleDownloadPDF = () => {
        const doc = new jsPDF();
        addPdfBranding(doc, "Tenant Occupancy Report");

        autoTable(doc, {
            startY: 65,
            head: [["ID", "Guest Name", "Unit", "Departure", "Monthly Rent"]],
            body: tenants.map(t => [
                t.tenant_id,
                t.guest_name,
                t.appartment_unite,
                t.departure_date?.substring(0, 10) || 'Active',
                `${parseFloat(t.rent).toFixed(2)} RWF`
            ]),
            foot: [['', '', '', 'EXPECTED REVENUE', `${totalRent.toFixed(2)} RWF`]],
            theme: 'grid'
        });

        doc.save(`GKM_Tenants_${Date.now()}.pdf`);
    };

    // ✨ FIXED: ACCURATE TENANT REPORT EXCEL WITH FULL EMBEDDED HEADERS
    const handleDownloadExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Occupancy Report");

        sheet.columns = [
            { id: 'A', width: 12 },
            { id: 'B', width: 28 },
            { id: 'C', width: 18 },
            { id: 'D', width: 18 },
            { id: 'E', width: 20 }
        ];

        // 🏢 ROW 1: COMPANY BANNER TITLE
        sheet.mergeCells('A1:E1');
        const titleCell = sheet.getCell('A1');
        titleCell.value = 'GKM INVEST COMPANY LTD';
        titleCell.font = { size: 20, bold: true, color: { argb: 'FFFFFF' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4F46E5' } }; // Indigo Theme
        sheet.getRow(1).height = 40;

        // 📊 ROW 2: REPORT SUBTITLE
        sheet.mergeCells('A2:E2');
        const reportTitle = sheet.getCell('A2');
        reportTitle.value = 'TENANT OCCUPANCY ANALYTICS REPORT';
        reportTitle.font = { size: 14, bold: true, color: { argb: '111827' } };
        reportTitle.alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getRow(2).height = 28;

        // 📅 ROW 3: TIMESTAMP LOG
        sheet.mergeCells('A3:E3');
        const dateCell = sheet.getCell('A3');
        dateCell.value = `Report Generated On: ${new Date().toLocaleString()}`;
        dateCell.font = { italic: true, size: 11, color: { argb: '6B7280' } };
        dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getRow(3).height = 22;

        // 📌 ROW 4: DATA TABLE HEADERS
        const headerRow = sheet.getRow(4);
        headerRow.values = ["ID", "Guest Name", "Unit/Apartment", "Departure Date", "Rent (RWF)"];
        headerRow.height = 26;
        headerRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: "FFFFFF" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "6366F1" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
        });

        // DATA ROWS
        tenants.forEach((t, idx) => {
            const rentVal = parseFloat(t.rent || 0);
            const row = sheet.addRow([
                t.tenant_id,
                t.guest_name,
                t.appartment_unite,
                t.departure_date ? t.departure_date.substring(0, 10) : 'Ongoing',
                rentVal
            ]);
            row.height = 20;
            row.alignment = { vertical: "middle" };
            row.getCell(1).alignment = { horizontal: "center" };
            row.getCell(4).alignment = { horizontal: "center" };
            row.getCell(5).numFmt = '"RWF" #,##0.00';

            if (idx % 2 === 0) {
                row.eachCell(cell => {
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FAFAFA" } };
                });
            }
        });

        // 🟩 SUMMARY TOTAL ROW
        const totalRow = sheet.addRow(['', 'EXPECTED REVENUE', '', '', totalRent]);
        totalRow.height = 24;
        totalRow.font = { bold: true };
        totalRow.eachCell(cell => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "ECFDF5" } };
            cell.alignment = { vertical: "middle" };
        });
        totalRow.getCell(2).alignment = { horizontal: "left" };
        totalRow.getCell(5).numFmt = '"RWF" #,##0.00';

        // 🧱 STRUCTURAL BORDERS BLOCK (Safely bypassing titles)
        sheet.eachRow((row, rowNum) => {
            if (rowNum < 4) return;
            row.eachCell(cell => {
                cell.border = {
                    top: { style: "thin", color: { argb: "E4E4E7" } },
                    left: { style: "thin", color: { argb: "E4E4E7" } },
                    bottom: { style: "thin", color: { argb: "E4E4E7" } },
                    right: { style: "thin", color: { argb: "E4E4E7" } }
                };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `GKM_Tenants_${Date.now()}.xlsx`);
    };

    return (
        <div className="report-container">
            <ManagerSidebar />
            <main className="report-main">
                <header className="report-header">
                    <div className="header-left">
                        <h1>Occupancy Analytics</h1>
                        <p>Overview of current residents and rental revenue.</p>
                    </div>
                    <div className="header-actions" style={{ display: 'flex', gap: '10px' }}>
                        <button className="export-btn accent" onClick={handleDownloadPDF}>
                            <FontAwesomeIcon icon={faDownload} /> Export PDF
                        </button>
                        <button className="export-btn accent" onClick={handleDownloadExcel}>
                            <FontAwesomeIcon icon={faDownload} /> Export Excel
                        </button>
                    </div>
                </header>

                <div className="report-grid">
                    <div className="report-card summary-card accent">
                        <div className="card-icon green"><FontAwesomeIcon icon={faUsers} /></div>
                        <div className="card-data">
                            <label>Expected Monthly Revenue</label>
                            <h3>{totalRent.toLocaleString(undefined, { minimumFractionDigits: 2 })} RWF</h3>
                        </div>
                    </div>
                    <div className="report-card filter-card">
                        <div className="input-field full">
                            <label>Status Filter</label>
                            <select value={filterByStatus} onChange={e => setFilterByStatus(e.target.value)}>
                                <option value="current">Active Tenants</option>
                                <option value="departed">Departure History</option>
                                <option value="all">Global List</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="table-wrapper shadow-sm">
                    <table className="modern-report-table">
                        <thead>
                            <tr><th>ID</th><th>Guest Name</th><th>Unit</th><th>Departure</th><th>Rent</th></tr>
                        </thead>
                        <tbody>
                            {tenants.map(t => (
                                <tr key={t.tenant_id}>
                                    <td>#{t.tenant_id}</td>
                                    <td>{t.guest_name}</td>
                                    <td>{t.appartment_unite}</td>
                                    <td>{t.departure_date ? t.departure_date.substring(0, 10) : 'Ongoing'}</td>
                                    <td>{parseFloat(t.rent).toFixed(2)} RWF</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </main>
        </div>
    );
};